import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface CartItem {
  product_id: string;
  product_name: string;
  price_per_kg: number;
  quantity_kg: number;
  pickup_date_id: string;
  pickup_date: string;
  pickup_start_time: string;
  pickup_end_time: string;
  location_name: string;
  location_address: string;
}

interface CartContextType {
  cart: CartItem[];
  addToCart: (item: CartItem) => void;
  updateQuantity: (productId: string, pickupDateId: string, quantity: number) => void;
  removeItem: (productId: string, pickupDateId: string) => void;
  clearCart: () => void;
  getCartTotal: () => number;
  getCartItemCount: () => number;
  getCartItem: (productId: string, pickupDateId: string) => CartItem | undefined;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = 'seaing_you_cart';

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<CartItem[]>(() => {
    // Load cart from sessionStorage on initialization
    const storedCart = sessionStorage.getItem(CART_STORAGE_KEY);
    return storedCart ? JSON.parse(storedCart) : [];
  });

  // Persist cart to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
  }, [cart]);

  const addToCart = (item: CartItem) => {
    setCart(prevCart => {
      // Check if item with same product and pickup date already exists
      const existingIndex = prevCart.findIndex(
        cartItem => cartItem.product_id === item.product_id && cartItem.pickup_date_id === item.pickup_date_id
      );

      if (existingIndex >= 0) {
        // Update existing item
        const newCart = [...prevCart];
        newCart[existingIndex] = item;
        return newCart;
      } else {
        // Add new item
        return [...prevCart, item];
      }
    });
  };

  const updateQuantity = (productId: string, pickupDateId: string, quantity: number) => {
    setCart(prevCart => {
      const itemIndex = prevCart.findIndex(
        item => item.product_id === productId && item.pickup_date_id === pickupDateId
      );

      if (itemIndex >= 0) {
        const newCart = [...prevCart];
        newCart[itemIndex] = {
          ...newCart[itemIndex],
          quantity_kg: quantity,
        };
        return newCart;
      }
      return prevCart;
    });
  };

  const removeItem = (productId: string, pickupDateId: string) => {
    setCart(prevCart =>
      prevCart.filter(item => !(item.product_id === productId && item.pickup_date_id === pickupDateId))
    );
  };

  const clearCart = () => {
    setCart([]);
    sessionStorage.removeItem(CART_STORAGE_KEY);
  };

  const getCartTotal = (): number => {
    return cart.reduce((total, item) => total + (item.quantity_kg * item.price_per_kg), 0);
  };

  const getCartItemCount = (): number => {
    return cart.length;
  };

  const getCartItem = (productId: string, pickupDateId: string): CartItem | undefined => {
    return cart.find(item => item.product_id === productId && item.pickup_date_id === pickupDateId);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        updateQuantity,
        removeItem,
        clearCart,
        getCartTotal,
        getCartItemCount,
        getCartItem,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
